/**
 * Sprint 8 — Public BizOne landing page.
 *
 * Message: Affordable POS + Inventory for Filipino Businesses.
 * - Hero with left headline + right device mockups (CSS/SVG only, demo data).
 * - Capabilities, How It Works, Hardware, Offline, Branding, Industries, Staff.
 * - Pricing strip is live from GET /api/billing/plans (never hardcoded).
 * - 7-day free trial. GCash with manual verification. No BIR accreditation claim.
 */
import { Link } from "react-router-dom";
import { useTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import { TESTIDS } from "@/constants/testIds";
import { ArrowUpRight, Check, Sun, Moon, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { TabletPosMockup, PhoneOwnerMockup } from "@/pages/landing/HeroDeviceMockups";
import IndustryShowcaseCarousel from "@/pages/landing/IndustryShowcaseCarousel";
import {
  CapabilitiesSection, HowItWorksSection, HardwareSection, OfflineSection,
  WorkspaceBrandingSection, IndustriesSection, StaffSection, OwnerViewSection,
  PublicPricingStrip, FinalCta,
} from "@/pages/landing/Sections";

// ---------- Public nav (§22) ----------
function NavBar() {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const links = [
    { to: "/#features", label: "Features" },
    { to: "/how-it-works", label: "How It Works" },
    { to: "/offline-pos", label: "Offline POS" },
    { to: "/pricing", label: "Pricing" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
  ];
  return (
    <nav className="sticky top-0 z-40 glass border-b border-border" data-testid="landing-nav">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
          <Logo size={28} />
          <span className="font-display text-base tracking-tight hidden sm:inline">BizOne</span>
        </Link>
        <div className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="hover:text-foreground transition-colors">{l.label}</Link>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            data-testid={TESTIDS.landing.themeToggle}
            className="hidden sm:flex h-9 w-9 rounded-md border border-border items-center justify-center hover:bg-muted transition-colors"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Link to="/login" data-testid={TESTIDS.landing.loginBtn}
                className="text-sm px-3 py-2 hover:bg-muted rounded-md transition-colors hidden sm:inline-flex">
            Sign In
          </Link>
          <Link to="/signup" data-testid={TESTIDS.landing.getStartedBtn}>
            <Button size="sm" className="gap-1.5">Start Free Trial<ArrowUpRight className="h-3.5 w-3.5" /></Button>
          </Link>
          <button className="md:hidden h-9 w-9 rounded-md border border-border flex items-center justify-center"
                  aria-label="Menu" data-testid="landing-mobile-menu"
                  onClick={() => setOpen((v) => !v)}>
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-border bg-background">
          <div className="max-w-7xl mx-auto px-5 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                    className="py-2 text-sm">{l.label}</Link>
            ))}
            <Link to="/login" onClick={() => setOpen(false)} className="py-2 text-sm">Sign In</Link>
          </div>
        </div>
      )}
    </nav>
  );
}

// ---------- Hero (§2 + §3) ----------
function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-border" data-testid="landing-hero">
      <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_20%_0%,rgba(99,102,241,0.10),transparent)] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-14 pb-16 lg:pt-24 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* LEFT */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/70 backdrop-blur px-3 py-1 text-xs">
              <span className="h-1.5 w-1.5 rounded-full bg-brand" />
              <span className="text-muted-foreground">7-Day Free Trial · No payment required</span>
            </div>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold leading-[0.95]"
                data-testid="landing-hero-headline">
              Affordable POS + Inventory
              <br />
              <span className="bg-gradient-to-r from-brand to-violet-500 bg-clip-text text-transparent">
                for Filipino Businesses.
              </span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-foreground/80 max-w-xl leading-relaxed">
              Manage sales, stocks, expenses, customers, utang and reports from the devices you already own.
              <span className="block mt-2 text-muted-foreground text-sm sm:text-base">
                No expensive traditional POS terminal required.
              </span>
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/signup" data-testid="landing-hero-start-btn">
                <Button size="lg" className="gap-1.5">
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/pricing" data-testid={TESTIDS.landing.pricingCta}>
                <Button size="lg" variant="outline">View Pricing</Button>
              </Link>
            </div>
            <div className="mt-6 flex items-center flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> No payment required</div>
              <div className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> Works on phone + tablet</div>
              <div className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> Benta · Stocks · Gastos · Utang · Kita</div>
            </div>
          </div>
          {/* RIGHT — device mockups */}
          <div className="lg:col-span-5">
            <div className="relative py-4 lg:py-8">
              <TabletPosMockup />
              <div className="absolute right-0 -bottom-6 sm:-bottom-8 lg:-right-4 lg:-bottom-10">
                <PhoneOwnerMockup />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Footer ----------
function Footer() {
  return (
    <footer className="border-t border-border bg-muted/30">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10 grid gap-8 md:grid-cols-[2fr_3fr]">
        <div>
          <div className="flex items-center gap-2.5"><Logo size={28} /><span className="font-display text-xl">BizOne</span></div>
          <p className="mt-3 text-sm text-muted-foreground max-w-sm">
            Affordable POS + Inventory for Filipino businesses. Use the devices you already have.
          </p>
          <p className="mt-6 text-[11px] text-muted-foreground">
            © {new Date().getFullYear()} BizOne. BizOne is not BIR accredited.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Product</div>
            <ul className="space-y-1.5">
              <li><Link to="/pricing" className="text-muted-foreground hover:text-foreground">Pricing</Link></li>
              <li><Link to="/how-it-works" className="text-muted-foreground hover:text-foreground">How It Works</Link></li>
              <li><Link to="/industries" className="text-muted-foreground hover:text-foreground">Industry Showcase</Link></li>
              <li><Link to="/offline-pos" className="text-muted-foreground hover:text-foreground">Offline POS</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Support</div>
            <ul className="space-y-1.5">
              <li><Link to="/faq" className="text-muted-foreground hover:text-foreground">FAQ</Link></li>
              <li><Link to="/contact" className="text-muted-foreground hover:text-foreground">Contact</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Get Started</div>
            <ul className="space-y-1.5">
              <li><Link to="/signup" className="text-muted-foreground hover:text-foreground">Start Free Trial</Link></li>
              <li><Link to="/login" className="text-muted-foreground hover:text-foreground">Sign In</Link></li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  const location = useLocation();
  // Smooth-scroll to the Industry Showcase (or any other anchor) when the
  // user lands with a hash — e.g. after /industries → /#industries redirect.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Defer to allow the section's DOM to mount after the hero/mockups.
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(t);
  }, [location.hash, location.key]);

  return (
    <div className="min-h-screen bg-background">
      <Seo blockKey="seo.home" fallback={{
        title: "BizOne — Affordable POS + Inventory for Filipino Businesses",
        description: "Use the phone or tablet you already have. Benta, stocks, gastos, utang, kita — all in one. 7-day free trial, no payment required.",
        robots: "index,follow",
      }} />
      <NavBar />
      <Hero />
      <IndustryShowcaseCarousel />
      <CapabilitiesSection />
      <HowItWorksSection />
      <HardwareSection />
      <OfflineSection />
      <WorkspaceBrandingSection />
      <IndustriesSection />
      <StaffSection />
      <OwnerViewSection />
      <PublicPricingStrip />
      <FinalCta />
      <Footer />
    </div>
  );
}
